<template>
  <resource-show-wrapper class="settings-show__wrapper" :title="$t('change_plan.page_title')">
    <template #actions>
      <v-btn @click="cancelButtonHandler" class="ml-auto cancel__btn" color="primary" outlined>
        {{ $t("generic.buttons.cancel") }}
      </v-btn>
      <v-btn :disabled="saveDisabled" @click="displayChangePlanModal = true" color="primary">
        {{ $t("generic.buttons.change") }}
      </v-btn>
    </template>
    <Modal
      :dialog-icon="isDowngrading ? 'info' : null"
      width="402"
      v-model="displayChangePlanModal"
      :title="$t('change_plan.dialog_title')"
      :subtitle="
        isDowngrading
          ? $t('change_plan.dialog_downgrade_subtitle')
          : $t('change_plan.dialog_subtitle', { amount: dueAmount })
      "
      :cta-text="isDowngrading ? $t('change_plan.dialog_downgrade_cta') : $t('change_plan.dialog_cta')"
      :cta-loading="changePlanLoading"
      @confirm="confirmChangePlan"
      @cancel="displayChangePlanModal = false">
    </Modal>
    <div class="d-flex">
      <!---- PLAN SELECT BLOCK --->
      <v-expansion-panels class="plan-select" accordion v-model="panelOpen">
        <v-overlay :value="planSelectBlockLoading" z-index="5" absolute class="system-bar-overlay">
          <v-progress-circular v-if="planSelectBlockLoading" indeterminate color="primary"></v-progress-circular>
        </v-overlay>

        <div v-if="planSelectBlockLoading" class="plan-select--placeholder"></div>

        <v-expansion-panel v-else readonly>
          <v-expansion-panel-header :expand-icon="''">
            <div v-if="!planSelectBlockLoading">
              <div class="change-plan__title">{{ $t("change_plan.title") }}</div>
              <div class="change-plan__subtitle" v-html="$t('change_plan.subtitle', { plan: currentPlan.name })"></div>

              <VaRadioContentCard
                class="change-plan__input"
                :value="selectedPlan"
                :items="planOptions"
                @change="changePlan">
                <template v-slot:[`Standard`]="{ item }">
                  <div class="plan-info-block">
                    <span class="plan-info-block__name">{{ item.name }}</span>
                    <span class="plan-info-block__price">{{
                      $t("change_plan.standard.price", { price: item.price })
                    }}</span>
                    <span class="plan-info-block__disclaimer">{{ $t("change_plan.standard.disclaimer") }}</span>
                    <ul class="plan-info-block__feature-list">
                      <li class="plan-info-block__feature-list__item">
                        <v-icon size="20" :color="'icon'">mdi-check</v-icon>
                        <span v-html="$tc('change_plan.standard.features.domains', item.formattedDomainsLimit)"></span>
                      </li>
                      <li class="plan-info-block__feature-list__item">
                        <v-icon size="20" :color="'icon'">mdi-check</v-icon>
                        <span
                          v-html="$tc('change_plan.standard.features.teammates', item.formattedTeammatesLimit)"></span>
                      </li>
                      <li class="plan-info-block__feature-list__item">
                        <v-icon size="20" :color="'icon'">mdi-check</v-icon>
                        <span>{{ $t("change_plan.standard.features.support") }}</span>
                      </li>
                    </ul>
                  </div>
                </template>

                <template v-slot:[`Pro`]="{ item }">
                  <div class="plan-info-block">
                    <span class="plan-info-block__name">{{ item.name }}</span>
                    <span class="plan-info-block__price">{{ $t("change_plan.pro.price", { price: item.price }) }}</span>
                    <span class="plan-info-block__disclaimer">{{ $t("change_plan.pro.disclaimer") }}</span>
                    <ul class="plan-info-block__feature-list">
                      <li class="plan-info-block__feature-list__item">
                        <v-icon size="20" :color="'icon'">mdi-check</v-icon>
                        <span v-html="$tc('change_plan.pro.features.domains', item.formattedDomainsLimit)"></span>
                      </li>
                      <li class="plan-info-block__feature-list__item">
                        <v-icon size="20" :color="'icon'">mdi-check</v-icon>
                        <span v-html="$tc('change_plan.pro.features.teammates', item.formattedTeammatesLimit)"></span>
                      </li>
                      <li class="plan-info-block__feature-list__item">
                        <v-icon size="20" :color="'icon'">mdi-check</v-icon>
                        <span>{{ $t("change_plan.pro.features.support") }}</span>
                      </li>
                    </ul>
                  </div>
                </template>

                <template v-slot:[`Agency`]="{ item }">
                  <div class="plan-info-block">
                    <span class="plan-info-block__name">{{ item.name }}</span>
                    <span class="plan-info-block__price">{{
                      $t("change_plan.agency.price", { price: item.price })
                    }}</span>
                    <span class="plan-info-block__disclaimer">{{ $t("change_plan.agency.disclaimer") }}</span>
                    <ul class="plan-info-block__feature-list">
                      <li class="plan-info-block__feature-list__item">
                        <v-icon size="20" :color="'icon'">mdi-check</v-icon>
                        <span v-html="$tc('change_plan.agency.features.domains', item.formattedDomainsLimit)"></span>
                      </li>
                      <li class="plan-info-block__feature-list__item">
                        <v-icon size="20" :color="'icon'">mdi-check</v-icon>
                        <span
                          v-html="$tc('change_plan.agency.features.teammates', item.formattedTeammatesLimit)"></span>
                      </li>
                      <li class="plan-info-block__feature-list__item">
                        <v-icon size="20" :color="'icon'">mdi-check</v-icon>
                        <span>{{ $t("change_plan.agency.features.support") }}</span>
                      </li>
                    </ul>
                  </div>
                </template>
              </VaRadioContentCard>

              <div class="d-flex align-center" @click="switchPanel">
                <span class="change-plan__expand-title">{{ $t("change_plan.all_features.title") }}</span>
                <v-btn
                  :ripple="false"
                  plain
                  small
                  class="expand__btn"
                  :class="[panelExpanded ? 'expand__btn--expanded' : '']">
                  <v-icon :color="'icon'">mdi-chevron-down</v-icon>
                </v-btn>
              </div>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <ul class="features__list px-0">
              <li class="features__list-item">
                <v-icon size="20" :color="'icon'">mdi-check</v-icon>
                <span class="features__list-item-text">{{ $t("change_plan.all_features.page_addons") }}</span>
              </li>
              <li class="features__list-item">
                <v-icon size="20" :color="'icon'">mdi-check</v-icon>
                <span class="features__list-item-text">{{ $t("change_plan.all_features.editing") }}</span>
              </li>
              <li class="features__list-item">
                <v-icon size="20" :color="'icon'">mdi-check</v-icon>
                <span class="features__list-item-text">{{ $t("change_plan.all_features.hosting") }}</span>
              </li>
              <li class="features__list-item">
                <v-icon size="20" :color="'icon'">mdi-check</v-icon>
                <span class="features__list-item-text">{{ $t("change_plan.all_features.design_elements") }}</span>
              </li>
              <li class="features__list-item">
                <v-icon size="20" :color="'icon'">mdi-check</v-icon>
                <span class="features__list-item-text">{{ $t("change_plan.all_features.responsive_pages") }}</span>
              </li>
              <li class="features__list-item">
                <v-icon size="20" :color="'icon'">mdi-check</v-icon>
                <span class="features__list-item-text">{{ $t("change_plan.all_features.support") }}</span>
              </li>
            </ul>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <!---- PLAN SELECT BLOCK END --->

      <!---- SUMMARY BLOCK --->
      <v-card class="summary-card">
        <v-overlay :value="summaryBlockLoading" z-index="5" absolute class="system-bar-overlay">
          <v-progress-circular v-if="summaryBlockLoading" indeterminate color="primary"></v-progress-circular>
        </v-overlay>
        <template v-if="summaryBlockLoading">
          <div class="summary-card--placeholder"></div>
        </template>
        <template v-else>
          <v-card-title>{{ $t("change_plan.summary.title") }}</v-card-title>
          <template v-if="!selectedPlan">
            <p class="summary-card__select-plan">{{ $t("change_plan.summary.disclaimer") }}</p>
          </template>
          <template v-else>
            <ul class="summary-card__price-list">
              <li
                class="summary-card__price-list-item"
                :class="[!(isDowngrading && planLimitsExceeded) ? 'spacing-bottom--16' : '']">
                <div class="d-flex flex-column">
                  <span class="summary-card__price-list-item__title">{{ sortedSummaryPlans[0].title }}</span>
                  <span class="summary-card__price-list-item__info">{{
                    $tc("change_plan.summary.domains", sortedSummaryPlans[0].formattedDomainsLimit)
                  }}</span>
                  <span class="summary-card__price-list-item__info">{{
                    $tc("change_plan.summary.teammates", sortedSummaryPlans[0].formattedTeammatesLimit)
                  }}</span>
                </div>
                <div class="summary-card__price-list-item__price">${{ sortedSummaryPlans[0].price }}</div>
              </li>

              <!--- Limits exceeded danger disclaimer --->
              <template v-if="isDowngrading && planLimitsExceeded">
                <div class="disclaimer-block disclaimer-block--error">
                  <div>
                    <v-icon color="error">mdi-information</v-icon>
                  </div>
                  <div class="d-flex flex-column">
                    <span class="disclaimer-block__title">{{ $t("change_plan.downgrading.title") }}</span>
                    <ul>
                      <li
                        v-if="domainsExceedLimit"
                        class="disclaimer-block__item"
                        v-html="
                          $tc('change_plan.downgrading.delete_domains', currentPlan.domains - selectedPlan.domainsLimit)
                        "></li>
                      <li
                        v-if="teammatesExceedLimit"
                        class="disclaimer-block__item"
                        v-html="
                          $tc(
                            'change_plan.downgrading.delete_teammates',
                            currentPlan.teammates - selectedPlan.teammatesLimit,
                          )
                        "></li>
                    </ul>
                    <span class="disclaimer-block__disclaimer">{{ $t("change_plan.downgrading.disclaimer") }}</span>
                  </div>
                </div>
              </template>
              <!--- Limits exceeded danger disclaimer END --->

              <li class="summary-card__price-list-item" :class="{ 'spacing-bottom--16': creditsUsed > 0 }">
                <div class="d-flex flex-column">
                  <span class="summary-card__price-list-item__title">{{ sortedSummaryPlans[1].title }}</span>
                  <span class="summary-card__price-list-item__info">
                    {{ $tc("change_plan.summary.domains", sortedSummaryPlans[1].formattedDomainsLimit) }}</span
                  >
                  <span class="summary-card__price-list-item__info">
                    {{ $tc("change_plan.summary.teammates", sortedSummaryPlans[1].formattedTeammatesLimit) }}
                  </span>
                </div>
                <div class="summary-card__price-list-item__price">-${{ sortedSummaryPlans[1].price }}</div>
              </li>

              <li v-if="creditsUsed > 0" class="summary-card__price-list-item">
                <div class="d-flex flex-column">
                  <span class="summary-card__price-list-item__title">{{ $t("change_plan.summary.credits_used") }}</span>
                </div>
                <div class="summary-card__price-list-item__price">-${{ creditsUsed }}</div>
              </li>
            </ul>

            <v-divider></v-divider>

            <div class="summary-card__due-today" :class="{ 'mb-0': isDowngrading || creditsReceived > 0 }">
              <span class="summary-card__due-today__text">{{ $t("change_plan.due", { date: dueDate }) }}</span>
              <span class="summary-card__due-today__price"> ${{ dueAmount }} </span>
            </div>

            <div v-if="creditsReceived > 0" class="summary-card__credits-received">
              <span class="summary-card__credits-received__text">{{ $t("change_plan.credits_received") }}</span>
              <span class="summary-card__credits-received__price"> +${{ creditsReceived }} </span>
            </div>

            <div v-if="creditsReceived > 0" class="disclaimer-block disclaimer-block--info">
              <div>
                <v-icon color="info">mdi-information</v-icon>
              </div>
              <div class="d-flex flex-column">
                <ul>
                  <li class="disclaimer-block__item" v-html="$t('change_plan.credits_info_block.text_1')"></li>
                  <li class="disclaimer-block__item" v-html="$t('change_plan.credits_info_block.text_2')"></li>
                </ul>
              </div>
            </div>

            <template v-if="!isDowngrading">
              <v-divider></v-divider>

              <ul class="summary-card__info-list">
                <!---- Separate the text below to list items html -->
                <li class="summary-card__info-list-item">
                  {{ $t("change_plan.summary.info_1", { amount: dueAmount }) }}
                </li>
                <li v-if="!isTrial" class="summary-card__info-list-item">
                  {{ $t("change_plan.summary.info_2") }}
                </li>
                <li class="summary-card__info-list-item">
                  {{ $t("change_plan.summary.info_3") }}
                </li>
                <li class="summary-card__info-list-item">
                  {{ $t("change_plan.summary.info_4") }}
                </li>
              </ul>
            </template>
          </template>
        </template>
      </v-card>
      <!---- SUMMARY BLOCK END --->
    </div>
  </resource-show-wrapper>
</template>

<script>
  // Components
  import ResourceShowWrapper from "@/components/resourceWrappers/ResourceShowWrapper";
  import VaRadioContentCard from "@/components/custom/VaRadioContentCard";
  import Modal from "@/components/custom/Modal";
  // Constants
  import { PAYMENT_PLANS } from "@/constant";
  import { mapGetters } from "vuex";
  import { getOrganizationResources } from "@/@core/utils/getOrganizationResources";
  import { format, isToday } from "date-fns";
  // Utility
  import { formErrorMessage } from "@/utility/formErrorMessage";
  import { goBack } from "@/utility/goBack";

  export default {
    name: "ChangePlan",
    components: {
      ResourceShowWrapper,
      VaRadioContentCard,
      Modal,
    },
    data() {
      return {
        displayChangePlanModal: false,
        panelOpen: false,
        selectedPlan: null,
        currentPlan: {},
        changePlanLoading: false,
        summaryLoading: false,
        summaryData: {},
        loading: false,
      };
    },
    computed: {
      planSelectBlockLoading() {
        return this.subscriptionDataLoading || this.loading;
      },
      summaryBlockLoading() {
        return this.summaryLoading || this.subscriptionDataLoading;
      },
      dueDate() {
        if (!this.summaryData.chargeAt) return "";

        const chargeAt = new Date(this.summaryData.chargeAt);

        return isToday(chargeAt) ? this.$t("change_plan.today") : format(chargeAt, "MM/dd/yyyy");
      },
      dueAmount() {
        return this.summaryData.amountRemaining / 100;
      },
      creditsReceived() {
        return this.summaryData.creditBalance / 100;
      },
      creditsUsed() {
        return this.summaryData.debitBalance / 100;
      },
      isDowngrading() {
        const currentPlanName = this.currentPlan?.name;
        const selectedPlanName = this.selectedPlan?.name;
        // No way to downgrade from Standard plan
        if (currentPlanName === PAYMENT_PLANS.STANDARD.name) return false;
        if (currentPlanName === PAYMENT_PLANS.PRO.name) {
          if (selectedPlanName === PAYMENT_PLANS.STANDARD.name) return true;

          return false;
        }
        if (currentPlanName === PAYMENT_PLANS.AGENCY.name) return true;

        return false;
      },
      // If organization currently has more domains than the selected plan allows
      domainsExceedLimit() {
        return this.currentPlan?.domains > this.selectedPlan?.domainsLimit;
      },
      // If organization currently has more teammates (users) than the selected plan allows
      teammatesExceedLimit() {
        return this.currentPlan?.teammates > this.selectedPlan?.teammatesLimit;
      },
      planLimitsExceeded() {
        return this.teammatesExceedLimit || this.domainsExceedLimit;
      },
      saveDisabled() {
        return !this.selectedPlan || this.planLimitsExceeded || this.summaryLoading;
      },
      panelExpanded() {
        return this.panelOpen === 0;
      },
      planOptions() {
        return Object.values(PAYMENT_PLANS)
          .filter(plan => plan.name !== PAYMENT_PLANS.BASIC.name)
          .filter(plan => plan.name !== this.currentPlan.name)
          .map(plan => ({
            ...plan,
            value: plan.name,
            slotName: plan.name,
          }));
      },
      /**
       * When a user chooses a plan that’s more expensive than their current plan,
       * the expensive plan should appear first and current plan second, so that the pricing
       * “equation” makes sense.
       */
      sortedSummaryPlans() {
        const currentPlan = {
          ...this.currentPlan,
          title: `Current plan (${this.currentPlan?.name})`,
        };

        const selectedPlan = {
          ...this.selectedPlan,
          title: `${this.selectedPlan?.name} (monthly)`,
        };

        const plans = [currentPlan, selectedPlan].sort((a, b) => b.price - a.price);

        return plans;
      },

      ...mapGetters({
        subscription: "billing/getSubscription",
        subscriptionDataLoading: "billing/getSubscriptionDataLoading",
        isTrial: "billing/getIsTrial",
      }),
    },
    watch: {
      // In case organization is changed in organization select, update billing data
      "$store.state.currentlyActiveOrganization.id": {
        immediate: true,
        async handler() {
          try {
            this.selectedPlan = null;
            await this.$store.dispatch("billing/loadSubscriptionData");
          } catch (e) {
            this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
          }
        },
      },
      subscription: {
        immediate: true,
        async handler(data) {
          if (!data) return;

          try {
            this.loading = true;
            const { teammates, domains } = await getOrganizationResources();

            this.currentPlan = {
              ...data,
              teammates,
              domains,
              price: this.isTrial ? 0 : data.amount / 100,
              name: data.planName,
            };
          } catch (e) {
            this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
          } finally {
            this.loading = false;
          }
        },
      },
    },
    methods: {
      async changePlan(value) {
        this.selectedPlan = PAYMENT_PLANS[value.toUpperCase()];
        await this.getChangePlanSummary(value.toLowerCase());
      },
      async getChangePlanSummary(plan) {
        this.summaryLoading = true;
        try {
          const { data } = await this.$admin.dataProvider.customHttpCall(
            "GET",
            `subscriptions/${this.subscription.id}/change_plan/${plan}/summary`,
          );

          this.summaryData = data;

          return data;
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.summaryLoading = false;
        }
      },
      async confirmChangePlan() {
        try {
          this.changePlanLoading = true;

          await this.$admin.dataProvider.customHttpCall("POST", `subscriptions/${this.subscription.id}/change_plan`, {
            plan: this.selectedPlan.name.toLowerCase(),
          });

          this.$admin.toast.success(this.$t("change_plan.plan_changed_successfully"));
          goBack(this.$router);
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.changePlanLoading = false;
          this.displayChangePlanModal = false;
        }
      },
      switchPanel() {
        this.panelOpen = this.panelExpanded ? false : 0;
      },
      cancelButtonHandler() {
        goBack(this.$router);
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";
  .cancel__btn {
    margin-right: 16px;
  }
  .expand__btn {
    margin-left: 4px;
    min-width: 24px !important;
    padding: 12px 0px !important;
    &--expanded {
      &::v-deep {
        .v-icon {
          transform: rotate(180deg);
        }
      }
    }
  }

  .change-plan {
    &__title {
      @include heading-3();
      color: get-color("headline");
      margin-bottom: 16px;
    }
    &__subtitle {
      @include text-body-1();
      color: get-color("body");
      margin-bottom: 16px;
    }
    &__expand-title {
      @include text-body-1();
      color: get-color("body");
      cursor: pointer;
    }
    &__input {
      margin-bottom: 16px;
    }
  }

  ::v-deep {
    .v-expansion-panel {
      &:before {
        box-shadow: 0 2px 10px 0 rgba(69, 64, 78, 0.1) !important;
      }
    }

    .v-expansion-panel-header {
      cursor: default !important;
    }
  }
  .features__list {
    list-style: none !important;
    &-item {
      display: flex;
      align-items: center;
      &:not(:last-child) {
        margin-bottom: 8px;
      }
    }
    &-item-text {
      @include text-body-2();
      color: get-color("body");
      margin-left: 7.5px;
    }
  }

  .plan-info-block {
    display: flex;
    flex-direction: column;
    &__name {
      @include text-body-1();
      color: get-color("headline");
    }
    &__price {
      @include heading-3();
      color: get-color("headline");
    }
    &__disclaimer {
      @include text-subtitle-2();
      color: get-color("subtitle");
      margin: 6px 0;
    }
    &__feature-list {
      list-style: none;
      padding-left: 0;
      &__item {
        display: flex;
        align-items: center;
        color: get-color("body");
        &:not(:last-child) {
          margin-bottom: 6px;
        }
        &::v-deep {
          .v-icon {
            margin-right: 10px;
          }
        }
      }
    }
  }

  .plan-select {
    align-self: flex-start;
    position: relative;
    &--placeholder {
      min-height: 366px;
      max-height: 366px;
      width: 100%;
      background: get-color("white");
    }
    &::v-deep {
      .v-expansion-panel {
        border-radius: 6px;
        &::after {
          border-top: none;
        }
      }
    }
  }

  .summary-card {
    max-width: 298px;
    min-width: 298px;
    margin-left: 24px;
    align-self: flex-start;
    position: relative;
    &--placeholder {
      min-height: 334px;
      max-height: 334px;
    }
    .v-card__title {
      margin-bottom: 16px;
    }
    &__select-plan {
      @include text-body-1();
      color: get-color("body");
    }
    &__price-list {
      list-style: none;
      padding-left: 0;
      margin-bottom: 16px;
      &-item {
        display: flex;
        & > div:first-child {
          flex: 1;
        }
        &__title {
          @include text-body-1();
          color: get-color("body");
          margin-bottom: 8px;
        }
        &__info {
          @include text-body-2();
          color: get-color("subtitle");
        }
        &__price {
          @include text-body-1-bold();
          color: get-color("body");
        }
      }
    }
    &__due-today {
      display: flex;
      margin: 16px 0;
      &__price {
        @include text-body-1-bold();
        color: get-color("body");
      }
      &__text {
        @include text-body-1();
        color: get-color("body");
        flex: 1;
      }
    }
    &__credits-received {
      display: flex;
      margin: 16px 0;
      &__price {
        @include text-body-1-bold();
        color: get-color("body");
      }
      &__text {
        @include text-body-1();
        color: get-color("body");
        flex: 1;
      }
    }
    &__info-list {
      margin-top: 16px;
      &-item {
        color: get-color("subtitle");
        @include text-caption();
      }
    }
  }

  .disclaimer-block {
    display: flex;
    padding: 8px 8px;
    &--info {
      background-color: get-color("accent-120");
      border: 1px solid get-color("accent");
    }
    &--error {
      background-color: get-color("error-120");
      border: 1px solid get-color("error");
    }
    border-radius: 4px;
    margin-bottom: 16px;
    margin-top: 8px;
    &::v-deep {
      .v-icon {
        margin-right: 6px;
      }
    }
    ul {
      list-style: disc;
    }
    &__title {
      @include text-caption-bold();
      color: get-color("headline");
      margin-bottom: 2px;
    }
    &__item {
      @include text-caption();
      color: get-color("headline");
      &:last-child {
        margin-bottom: 2px;
      }
    }
    &__disclaimer {
      @include text-caption();
      color: get-color("headline");
    }
  }
</style>
